import type { CategoriesViewProps, GameMinimalProps, GameProps } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { useInView } from 'react-intersection-observer';
import { Typography as MuiTypography, Button as MuiButton, useMediaQuery } from '@mui/material';
import { Link } from '../../components/Link';
import { Title } from '../../components/Title';
import { Card } from '../../components/Card';
import { useAppContext } from '../../contexts/AppContext';
import { CardTypeEnum } from '../../types/enum';
import useLocalStorage from '../../utils/use-local-storage';
import { useCursorContext } from '../../contexts/CursorContext';
import { COLLECTIONS_B_BOTTOM, COLLECTIONS_B_MIDDLE, COLLECTIONS_B_TOP } from '../../types/static';
import useFlattenedGameData from '../../utils/useFlattenArray';
import { Box1, Box1a, Box2, Box2a, Box3, Box3a, Box4, Box4a, Box5, Box6, Box7, CardActionAreaFlat, CardAssetFlat, CardContentFlat, CardPaperColorElevationFlat, CardSkeletonGrid, CardSkeletonStyled, CardTypographyFlat, EditorsSection, FeaturedListSection, HeaderSection, MiddleSectionLeft, MiddleSectionRight, Root } from './styled';

const AdvertisementSlot = dynamic<any>(
  () => import('../../components/AdvertisementSlot').then((mod) => mod.AdvertisementSlot),
  {
    ssr: false,
  }
);
const CollectionSection = dynamic<any>(
  () => import('../../components/CollectionSection').then((mod) => mod.CollectionSection),
  {
    ssr: false,
  }
);
const CarouselList = dynamic<any>(() => import('../../components/CarouselList').then((mod) => mod.CarouselList), {
  ssr: false,
});
const CarouselThumbs = dynamic<any>(() => import('../../components/CarouselThumbs/CarouselThumbs'), {
  ssr: true,
});

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const Button = React.forwardRef<HTMLButtonElement, any>((props, ref) =>
  useHighlightHandlers(MuiButton, { forwardedRef: ref, ...props })
);


const AdvertisementSlotStyled = styled(({...rest}: any) => {
  return <AdvertisementSlot {...rest} />;
})(({ theme }) => ({
  boxShadow: theme.shadows[0],
}));

const CardStyled = styled(Card)(({ theme, template, hoverEffect, cardBorder }: any) => ({
  width: '100%',
  backgroundColor: theme.theme_component_card_background_color || theme.palette.background.paper,
  boxShadow: theme.theme_component_card_box_shadow || theme.shadows[3],
  borderRadius: template === 'big' ? 0 : `${theme.theme_component_card_border_radius}px` || theme.shape.borderRadius,
  border: cardBorder || `${theme.theme_component_card_content_border}px` || 'none',
  borderStyle: theme.theme_component_card_content_border ? 'solid' : 'none',
  borderColor: theme.theme_component_card_content_border ? theme.theme_component_card_content_border_color : 'none',
  transition: `transform ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
  '&:hover': hoverEffect && {
    transform: 'scale3d(1.05, 1.05, 1)',
  },
}));

const AdvertisementSlotBottom = (props: any) => {
  return (
    <Box7 elevation={0}>
      <AdvertisementSlotStyled
        slotIdentifier={props.id || "idhb-bottom-970"}
        variant={props.variant}
        elevation={1}
        configuration={COLLECTIONS_B_TOP}
      />
    </Box7>
  )
}

const AdvertisementSlotMiddle = (props: any) => {
  return (
    <Box4 className='testbox4' elevation={1}>
      <AdvertisementSlotStyled slotIdentifier={props.id || "idhb-middle-336"} variant="336x280" elevation={2} configuration={COLLECTIONS_B_MIDDLE} />
    </Box4>
  )
}

const AdvertisementSlotTop = (props: any) => {
  return (
    <Box3a elevation={1}>
      <AdvertisementSlotStyled
        slotIdentifier={props.id || "idhb-right-300_1"}
        variant={props.variant}
        elevation={2}
        configuration={COLLECTIONS_B_BOTTOM}
      />
    </Box3a>
  )
}

export const CollectionsB = (props: CategoriesViewProps) => {
  const { collections } = props;
  const { t } = useTranslation('collections_b');
  const { ref, inView } = useInView();
  const appContext = useAppContext();
  const { enabled } = useCursorContext();
  const { assetUrl, componentOptions, project } = appContext.useConfig();
  const header_lift =  project.features.ads.header_lift;
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const isBetweenMdAndLg = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const { panel } = useCursorContext();

  const buttonMiddleLeftRef = React.useRef<HTMLButtonElement | null>(null);
  const buttonMiddleRightRef = React.useRef<HTMLButtonElement | null>(null);
  const editorCardRefs = React.useRef<React.Ref<HTMLAnchorElement> | []>([]);
  const middleLeftCardRefs = React.useRef<React.Ref<HTMLAnchorElement> | []>([]);
  const middleRightCardRefs = React.useRef<React.Ref<HTMLAnchorElement> | []>([]);

  // @ts-ignore
  editorCardRefs.current = [...Array(12)].map((game, index: number) => {
    // @ts-ignore
    return editorCardRefs.current[index] ?? React.createRef();
  });

  // @ts-ignore
  middleLeftCardRefs.current = [...Array(12)].map((game, index: number) => {
    // @ts-ignore
    return middleLeftCardRefs.current[index] ?? React.createRef();
  });

  // @ts-ignore
  middleRightCardRefs.current = [...Array(12)].map((game, index: number) => {
    // @ts-ignore
    return middleRightCardRefs.current[index] ?? React.createRef();
  });

  React.useEffect(() => {
    if (header_lift) {
      import('../../utils/ads-request').then(({ adsRequest }) => {
        const obj: Record<string, boolean> = {
          'idhb-right-300_1': !componentOptions.component_right_ads_disable || !componentOptions.component_right_ads_tablet_disable || !componentOptions.component_right_ads_mobile_disable,
          'idhb-middle-336': !componentOptions.component_middle_center_ads_disable || !componentOptions.component_middle_center_ads_tablet_disable || !componentOptions.component_middle_center_ads_mobile_disable,
          'idhb-bottom-970': !componentOptions.component_bottom_ads_disable || !componentOptions.component_bottom_ads_tablet_disable || !componentOptions.component_bottom_ads_mobile_disable
        };
        
        const adsList = Object.keys(obj).filter(key => obj[key]);
        adsRequest(adsList);
      });
    }
  }, [header_lift, componentOptions]);

  const cardType = componentOptions?.component_game_carousel_card_type;

  const [lastPlayedGames] = useLocalStorage<GameProps[] | undefined>('lastplayed:current', []);
  const showLastPlayedCarousel = componentOptions?.component_homepage_show_last_played_carousel;
  const recommendedCollection = collections.find((c) => c.code.includes('recommended')) || { games: [] };
  const homeCollections = collections?.filter((c) => !c.code.includes('recommended'));

  const flattenedData = useFlattenedGameData(lastPlayedGames || []);

  const headerSectionNotAds = () => {
    if(panel) return false;
      return !componentOptions.component_ads_disabled ? lg ? componentOptions.component_right_ads_disable : isBetweenMdAndLg ? componentOptions.component_right_ads_tablet_disable : componentOptions.component_right_ads_mobile_disable : componentOptions.component_ads_disabled
  }

  const rootSectionNotAds = () => {
    if(panel) return false;
      return !componentOptions.component_ads_disabled ? lg ? componentOptions.component_middle_center_ads_disable : isBetweenMdAndLg ? componentOptions.component_middle_center_ads_tablet_disable : componentOptions.component_middle_center_ads_mobile_disable : componentOptions.component_ads_disabled
  }

  return (
    <Root ref={ref} isfullscreen={rootSectionNotAds()}>
      <Box1 elevation={0}>
        <HeaderSection className="header-section" isfullscreen={headerSectionNotAds()}>
          <Box1a elevation={1}>
            <CarouselThumbs
              items={
                showLastPlayedCarousel && flattenedData && flattenedData.length > 0
                  ? [
                      ...flattenedData,
                      ...recommendedCollection.games.map((game) => ({
                        ...game,
                        trend: true,
                      })),
                    ]
                  : [...Array(4)].map((slot, index) => homeCollections[0]?.games?.[index])
              }
            />
          </Box1a>
          <Box2a elevation={1}>
            <Title className='birasitest' component="h1" variant="h2" align="left" style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
              <Link
                href={`/${homeCollections[1]?.type}/${homeCollections[1]?.slug}`}
                title={homeCollections[1]?.name || undefined}
                underline="hover"
              >
                {homeCollections[1]?.name || 'Popular games'}
              </Link>
            </Title>
            <EditorsSection>
              {[...Array(homeCollections[1]?.games?.length > 12 ? 12 : homeCollections[1]?.games?.length)]
                .map((slot, index) => homeCollections[1]?.games?.[index])
                .map((item: GameMinimalProps, index) =>
                  item ? (
                    <CardPaperColorElevationFlat key={index} elevation={1}>
                      <CardActionAreaFlat component={Link} href={`${enabled ? '#' : `${item.slug}`}`} title={item.name || undefined}>
                        <CardAssetFlat
                          src={`${assetUrl}${item.images.cover16x9 || item.images.cover}/`}
                          alt={item.name || 'Cover'}
                          width={componentOptions.component_card_width || 512}
                          height={componentOptions.component_card_height || 288}
                          layout="responsive"
                          sizes="(min-width:1200px) 8.333vw, (min-width:900px) 16.333vw, (min-width:600px) 25vw, 50vw"
                        />
                        <CardContentFlat>
                          <CardTypographyFlat
                            // @ts-ignore
                            ref={editorCardRefs.current[index]}
                            variant="caption"
                            component="span"
                            noWrap
                          >
                            {item.name}
                          </CardTypographyFlat>
                        </CardContentFlat>
                      </CardActionAreaFlat>
                    </CardPaperColorElevationFlat>
                  ) : (
                    <CardSkeletonGrid key={index} elevation={2} />
                  )
                )}
            </EditorsSection>
          </Box2a>
          {panel ? (
            <AdvertisementSlotTop variant={md ? '300x600' : '320x50'} />
          ) : (
            <>
              {!componentOptions.component_ads_disabled && (
                <>
                  {(lg && !componentOptions.component_right_ads_disable) && (
                    <AdvertisementSlotTop id={componentOptions.component_right_ads_id} variant={md ? '300x600' : '320x50'} />
                  )}
                  {(isBetweenMdAndLg && !componentOptions.component_right_ads_tablet_disable) && (
                    <AdvertisementSlotTop id={componentOptions.component_right_ads_id} variant={md ? '300x600' : '320x50'} />
                  )}
                  {(mdDown && !componentOptions.component_right_ads_mobile_disable) && (
                    <AdvertisementSlotTop id={componentOptions.component_right_ads_id} variant={md ? '300x600' : '320x50'} />
                  )}
                </>
              )}
            </>
          )}
          <Box4a elevation={0}>
            <Title component="h2" variant="h2" align="left" disableGutters style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
              <Link
                href={`/${homeCollections[2]?.type}/${homeCollections[2]?.slug}`}
                title={homeCollections[2]?.name || undefined}
                underline="hover"
              >
                {homeCollections[2]?.name || 'New releases'}
              </Link>
            </Title>
            <FeaturedListSection>
              {[...Array(mdDown ? 6 : 5)]
                .map((slot, index) => homeCollections[2]?.games?.[index])
                .map((item: GameMinimalProps, index) =>
                  item?.id ? (
                    <CardStyled
                      key={index}
                      href={`${item.slug}`}
                      src={`${assetUrl}${item.images.cover16x9 || item.images.cover}/`}
                      alt={item.name || 'Cover'}
                      template={cardType || CardTypeEnum.DEFAULT}
                      title={item.name || undefined}
                      width={componentOptions.component_card_width || 512}
                      height={componentOptions.component_card_height || 288}
                      layout="responsive"
                      sizes="(min-width:900px) 14vw, (min-width:600px) 33.333vw, 50vw"
                      typographyOptions={{
                        align: 'left',
                      }}
                    />
                  ) : (
                    <CardSkeletonStyled key={index} elevation={2} />
                  )
                )}
            </FeaturedListSection>
          </Box4a>
        </HeaderSection>
      </Box1>
      <Box2 elevation={0}>
        {inView && (
          <CollectionSection>
            <Title component="h2" variant="h2" align="left" style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
              <Link
                href={`/${homeCollections[3]?.type}/${homeCollections[3]?.slug}`}
                title={homeCollections[3]?.name || undefined}
                underline="hover"
              >
                {homeCollections[3]?.name || 'Puzzle'}
              </Link>
            </Title>
            <CarouselList
              padding={theme.spacing(2)}
              items={[...Array(12)].map((slot, index) => homeCollections[3]?.games?.[index])}
              cardOptions={{
                width: componentOptions.component_card_width || 512,
                height: componentOptions.component_card_height || 288,
                sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                layout: 'responsive',

                template: cardType || CardTypeEnum.EXTENSIVE,
                typographyOptions: {
                  align: 'left',
                },
              }}
            />
          </CollectionSection>
        )}
      </Box2>
      <Box3 elevation={1}>
        <Title component="h2" variant="h2" align="left" style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
          <Link
            href={`/${homeCollections[4]?.type}/${homeCollections[4]?.slug}`}
            title={homeCollections[4]?.name || undefined}
            underline="hover"
          >
            {collections[4]?.name || 'Simulation'}
          </Link>
        </Title>
        <MiddleSectionLeft>
          {[...Array(6)]
            .map((slot, index) => homeCollections[4]?.games?.[index])
            .map((item: GameMinimalProps, index) =>
              item ? (
                <CardPaperColorElevationFlat key={index} elevation={2}>
                  <CardActionAreaFlat component={Link} href={`${item.slug}`} title={item.name || undefined}>
                    <CardAssetFlat
                      src={`${assetUrl}${item.images.cover16x9 || item.images.cover}/`}
                      alt={item.name || 'Cover'}
                      width={componentOptions.component_card_width || 512}
                      height={componentOptions.component_card_height || 288}
                      layout="responsive"
                      sizes="(min-width:1200px) 8.333vw, (min-width:900px) 16.666vw, (min-width:600px) 33.333vw, 50vw"
                    />
                    <CardContentFlat>
                      <CardTypographyFlat
                        // @ts-ignore
                        ref={middleRightCardRefs.current[index]}
                        variant="caption"
                        component="span"
                        noWrap
                      >
                        {item.name}
                      </CardTypographyFlat>
                    </CardContentFlat>
                  </CardActionAreaFlat>
                </CardPaperColorElevationFlat>
              ) : (
                <CardSkeletonGrid key={index} elevation={2} />
              )
            )}
        </MiddleSectionLeft>
        {homeCollections[4]?.type && (
          <Button
            ref={buttonMiddleLeftRef}
            color="primary"
            variant="contained"
            disableElevation={true}
            sx={{
              marginTop: 'auto',
              borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
            }}
            // Can't use href to send an object; https://github.com/mui/material-ui/issues/24106
            component={Link}
            href={`/${homeCollections[4]?.type}/${homeCollections[4]?.slug}`}
            title={homeCollections[4]?.name || undefined}
          >
            {t('view_all_label', 'View all')}
          </Button>
        )}
      </Box3>
      {panel ? (<AdvertisementSlotMiddle />) : (
        <>
          {!componentOptions.component_ads_disabled && (
            <>
              {(lg && !componentOptions.component_middle_center_ads_disable) && (
                <AdvertisementSlotMiddle id={componentOptions.component_center_middle_ads_id} />
              )}
              {(isBetweenMdAndLg && !componentOptions.component_middle_center_ads_tablet_disable) && (
                <AdvertisementSlotMiddle id={componentOptions.component_center_middle_ads_id} />
              )}
              {(mdDown && !componentOptions.component_middle_center_ads_mobile_disable) && (
                <AdvertisementSlotMiddle id={componentOptions.component_center_middle_ads_id} />
              )}
            </>
          )}
        </>
      )}
      
      <Box5 elevation={1}>
        <Title component="h2" variant="h2" align="left" style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
          <Link
            href={`/${homeCollections[5]?.type}/${homeCollections[5]?.slug}`}
            title={homeCollections[5]?.name || undefined}
            underline="hover"
          >
            {homeCollections[5]?.name || 'Hypercasual'}
          </Link>
        </Title>
        <MiddleSectionRight>
          {[...Array(6)]
            .map((slot, index) => homeCollections[5]?.games?.[index])
            .map((item: GameMinimalProps, index) =>
              item ? (
                <CardPaperColorElevationFlat key={index} elevation={2}>
                  <CardActionAreaFlat
                    key={index}
                    component={Link}
                    href={`${item.slug}`}
                    title={item.name || undefined}
                  >
                    <CardAssetFlat
                      src={`${assetUrl}${item.images.cover16x9 || item.images.cover}/`}
                      alt={item.name || 'Cover'}
                      width={componentOptions.component_card_width || 512}
                      height={componentOptions.component_card_height || 288}
                      layout="responsive"
                      sizes="(min-width:1200px) 8.333vw, (min-width:900px) 16.666vw, (min-width:600px) 33.333vw, 50vw"
                    />
                    <CardContentFlat>
                      <CardTypographyFlat
                        // @ts-ignore
                        ref={middleLeftCardRefs.current[index]}
                        variant="caption"
                        component="span"
                        noWrap
                      >
                        {item.name}
                      </CardTypographyFlat>
                    </CardContentFlat>
                  </CardActionAreaFlat>
                </CardPaperColorElevationFlat>
              ) : (
                <CardSkeletonGrid key={index} elevation={2} />
              )
            )}
        </MiddleSectionRight>
        {homeCollections[5]?.type && (
          <Button
            ref={buttonMiddleRightRef}
            color="primary"
            variant="contained"
            disableElevation={true}
            sx={{
              marginTop: 'auto',
              borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
            }}
            // Can't use href to send an object; https://github.com/mui/material-ui/issues/24106
            component={Link}
            href={`/${homeCollections[5]?.type}/${homeCollections[5]?.slug}`}
            title={homeCollections[5]?.name || undefined}
          >
            {t('view_all_label', 'View all')}
          </Button>
        )}
      </Box5>
      <Box6 elevation={0}>
        {inView && (
          <CollectionSection>
            <Title component="h2" variant="h2" align="left" style={{ paddingLeft: "16px", borderBottomWidth: 1}}>
              <Link
                href={`/${homeCollections[6]?.type}/${homeCollections[6]?.slug}`}
                title={homeCollections[6]?.name || undefined}
                underline="hover"
              >
                {homeCollections[6]?.name || 'Multiplayer'}
              </Link>
            </Title>
            <CarouselList
              padding={theme.spacing(2)}
              items={[...Array(12)].map((slot, index) => homeCollections[6]?.games?.[index])}
              cardOptions={{
                width: componentOptions.component_card_width || 512,
                height: componentOptions.component_card_height || 288,
                sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                layout: 'responsive',
                typographyOptions: {
                  align: 'left',
                },
              }}
            />
          </CollectionSection>
        )}
      </Box6>
      {panel ? (
        <AdvertisementSlotBottom variant={lg ? '970x250' : md ? '728x90' : '320x50'} />
      ) : (
        <>
          {!componentOptions.component_ads_disabled && (
            <>
              {(lg && !componentOptions.component_bottom_ads_disable) && (
                <AdvertisementSlotBottom id={componentOptions.component_bottom_ads_id} variant={lg ? '970x250' : md ? '728x90' : '320x50'} />
              )}
              {(isBetweenMdAndLg && !componentOptions.component_bottom_ads_tablet_disable) && (
                <AdvertisementSlotBottom id={componentOptions.component_bottom_ads_id} variant={lg ? '970x250' : md ? '728x90' : '320x50'} />
              )}
              {(mdDown && !componentOptions.component_bottom_ads_mobile_disable) && (
                <AdvertisementSlotBottom id={componentOptions.component_bottom_ads_id} variant={lg ? '970x250' : md ? '728x90' : '320x50'} />
              )}
            </>
          )}
        </>
      )}
      
    </Root>
  );
};
